import { useToken } from '@chakra-ui/react';
import { flexRender, type Row } from '@tanstack/react-table';
import type { VirtualItem } from '@tanstack/react-virtual';
import classNames from 'classnames';

import { useChartTheme } from '../../utilities';
import styles from './table-chart.module.css';

export const useGetBodyRows = <TData extends object = object>({
  items,
  rows,
  enableRowSelection,
  showZebraStripe,
  resolveTextAlign,
  onSelectRow,
  onRenderRow,
  autoFitColumns,
  scrollRef,
}: {
  items: VirtualItem[];
  rows: Row<TData>[];
  enableRowSelection: boolean;
  showZebraStripe: boolean;
  resolveTextAlign: (
    columnId: string
  ) => 'left' | 'center' | 'right' | undefined;
  onSelectRow?: (data: object) => void;
  onRenderRow?: (params: {
    rowContent: React.ReactNode;
    rowData: TData;
    rowIndex: number;
    gridRef: HTMLDivElement | null;
  }) => React.ReactNode;
  autoFitColumns: boolean;
  scrollRef: React.RefObject<HTMLDivElement>;
}) => {
  const getRowClassName = (index: number, isSelected: boolean) => {
    return classNames(
      showZebraStripe && index % 2 === 0 ? styles.rowEven : styles.rowOdd,
      enableRowSelection && isSelected && styles.rowSelected
    );
  };

  const [lightZebra] = useToken('colors', ['lightBlue.50']);
  const { theme } = useChartTheme();
  const zebraStripeColor = theme === 'dark' ? '#1e2128' : lightZebra;
  const getZebraStripeStyles = (index: number) => {
    if (!showZebraStripe || index % 2 !== 0 || !zebraStripeColor) {
      return {};
    }
    return { '--zebra-stripe-color': zebraStripeColor };
  };

  return items.map((virtualRow, index) => {
    const row = rows[virtualRow.index];
    if (!row) return null;
    const isLastRow = virtualRow.index === rows.length - 1;

    const defaultRowContent = (
      <tr
        key={row.id}
        className={getRowClassName(virtualRow.index, row.getIsSelected())}
        style={{
          height: `${virtualRow.size}px`,
          transform: `translateY(${virtualRow.start - index * virtualRow.size}px)`,
          cursor: 'pointer',
          ...getZebraStripeStyles(virtualRow.index),
        }}
      >
        {row.getVisibleCells().map((cell, cellIdx) => (
          <td
            key={cell.id}
            className={classNames(
              styles.cell,
              cellIdx === 0 && styles.firstCol,
              isLastRow && styles.lastRowCell
            )}
            style={{
              ...(autoFitColumns
                ? {}
                : {
                    width: `${cell.column.getSize()}px`,
                    maxWidth: `${cell.column.getSize()}px`,
                  }),
              ...(resolveTextAlign(cell.column.id)
                ? { textAlign: resolveTextAlign(cell.column.id) }
                : {}),
            }}
            onClick={() => {
              onSelectRow?.(row.original);
            }}
          >
            {flexRender(cell.column.columnDef.cell, cell.getContext())}
          </td>
        ))}
      </tr>
    );

    if (!onRenderRow) return defaultRowContent;

    return onRenderRow({
      rowContent: defaultRowContent,
      rowData: row.original,
      rowIndex: virtualRow.index,
      gridRef: scrollRef.current,
    });
  });
};
